import Util from '../../../../forecast-app/shared/util/util';
import {createFragmentContainer, graphql} from 'react-relay';
import {useIntl} from 'react-intl';
import {useEffect} from 'react';
import ProjectFormatter from '../../../../forecast-app/shared/util/export-formatters/ProjectFormatter';
import {hasFeatureFlag} from '../../../../forecast-app/shared/util/FeatureUtil';
import {useRemappingFormatMessage} from '../../../../forecast-app/shared/util/FinancialInternationalisationUtil';

const exportToCsv = (startDate, endDate, viewer, boolEyeOptions, intl) => {
	const formatMessage = useRemappingFormatMessage(intl);
	const new_csv_format = hasFeatureFlag('new_csv_format');
	const format = new ProjectFormatter(intl);

	const currency = viewer.company.currency;
	const projects = viewer.company.financialBasedProjects.edges.map(edge => edge.node);

	const formatColumnName = name => {
		return new_csv_format ? name.replaceAll(' ', '_').replaceAll('(h)', '(min)').toLowerCase() : name;
	};

	const addColumn = (row, visible, name, value) => {
		if (visible) {
			row[formatColumnName(name)] = value;
		}
	};

	if (projects.length > 0) {
		const periodData = projects.map(project => {
			const row = {};
			const {financialNumbers} = project;

			addColumn(row, true, 'Project ID', `P${project.companyProjectId}`);
			addColumn(row, true, 'Project', project.name);
			// Project overview
			addColumn(
				row,
				boolEyeOptions.winProbability,
				'Win Probability',
				format._formatPercentageFromFraction(project.baselineWinChance)
			);
			addColumn(
				row,
				boolEyeOptions.dates,
				'Start Date',
				format._formatDate(project.projectStartDay, project.projectStartMonth, project.projectStartYear)
			);
			addColumn(
				row,
				boolEyeOptions.dates,
				'End Date',
				format._formatDate(project.projectEndDay, project.projectEndMonth, project.projectEndYear)
			);
			addColumn(
				row,
				boolEyeOptions.client,
				formatMessage({id: 'insights.component.list.column.client'}),
				project.client ? project.client.name : ''
			);
			addColumn(
				row,
				boolEyeOptions.status,
				formatMessage({id: 'common.status'}),
				project.currentProjectStatus ? Util.getProjectStatusColorName(project.currentProjectStatus.color) : ''
			);
			addColumn(
				row,
				boolEyeOptions.projectOwner,
				formatMessage({id: 'common.project_owner'}),
				format._formatProjectOwner(project.projectPersons)
			);
			addColumn(
				row,
				boolEyeOptions.stage,
				formatMessage({id: 'insights.component.list.column.status'}),
				Util.getProjectStageTranslation(project.status, intl)
			);
			addColumn(
				row,
				boolEyeOptions.labels,
				formatMessage({id: 'insights.component.list.column.labels'}),
				format._formatLabels(project.projectLabels)
			);
			addColumn(
				row,
				boolEyeOptions.allocations,
				formatMessage({id: 'scheduling.menu.allocations'}),
				format._formatTime(financialNumbers.allocationMinutes)
			);
			addColumn(
				row,
				boolEyeOptions.progress,
				formatMessage({id: 'common.progress'}),
				format._formatPercentageFromFraction(project.completion / 100)
			);
			addColumn(
				row,
				boolEyeOptions.progressEom,
				formatMessage({id: 'project_portfolio_report.progress_eom'}),
				format._formatPercentageFromFraction(project.progressEndOfTheMonth / 100)
			);
			// Project Details
			addColumn(
				row,
				boolEyeOptions.registeredHours,
				'Registered',
				format._formatTime(financialNumbers.registeredMinutes)
			);
			addColumn(
				row,
				boolEyeOptions.remainingHours,
				'Remaining (h)',
				format._formatTime(financialNumbers.remainingMinutes)
			);
			addColumn(
				row,
				boolEyeOptions.forecastedHours,
				'Forecasted (h)',
				format._formatTime(financialNumbers.forecastMinutes)
			);
			addColumn(
				row,
				boolEyeOptions.totalScope,
				formatMessage({id: 'project_portfolio_report.total_scope'}),
				format._formatTime(financialNumbers.scopeTotalMinutes)
			);
			addColumn(
				row,
				boolEyeOptions.approvedScope,
				formatMessage({id: 'project_portfolio_report.approved_scope'}),
				format._formatTime(financialNumbers.scopeApprovedMinutes)
			);
			addColumn(row, boolEyeOptions.baselineHours, 'Baseline (h)', format._formatTime(financialNumbers.baselineMinutes));
			// Financials
			addColumn(
				row,
				boolEyeOptions.budgetType,
				formatMessage({id: 'new_project_modal.budget_type'}),
				format._formatBudgetType(project)
			);
			addColumn(
				row,
				boolEyeOptions.rateCard,
				formatMessage({id: 'insights.component.list.column.rateCard'}),
				project.rateCard ? project.rateCard.name : ''
			);
			// Baseline
			addColumn(
				row,
				boolEyeOptions.baselineRevenue,
				formatMessage({id: 'project_budget.baseline'}) + ' ' + formatMessage({id: 'project_budget.revenue'}),
				format._formatCurrency(financialNumbers.baselineRevenue)
			);
			addColumn(row, boolEyeOptions.baselineCost, 'Baseline Cost', format._formatCurrency(financialNumbers.baselineCost));
			addColumn(
				row,
				boolEyeOptions.baselineProfit,
				'Baseline Profit',
				format._formatCurrency(financialNumbers.baselineProfit)
			);
			addColumn(
				row,
				boolEyeOptions.baselineMargin,
				'Baseline Margin',
				format._formatPercentageFromFraction(financialNumbers.baselineMargin)
			);
			// Invoiced
			addColumn(row, boolEyeOptions.invoiced, 'Invoiced', format._formatCurrency(financialNumbers.invoiced));
			addColumn(row, boolEyeOptions.paid, 'Invoice Paid', format._formatCurrency(financialNumbers.paid));
			// Revenue Recognition
			addColumn(
				row,
				boolEyeOptions.recognitionLockedRevenue,
				formatMessage({id: 'project_budget.recognition_locked_revenue'}),
				format._formatCurrency(financialNumbers.lockedRevenue)
			);
			addColumn(
				row,
				boolEyeOptions.recognitionOpenRevenue,
				formatMessage({id: 'project_budget.recognition_remaining_revenue'}),
				format._formatCurrency(financialNumbers.recognitionOpenRevenue)
			);
			addColumn(
				row,
				boolEyeOptions.recognitionAmount,
				formatMessage({id: 'project_budget.recognition_amount'}),
				format._formatCurrency(financialNumbers.recognitionAmount)
			);
			// Fixed Price
			addColumn(row, boolEyeOptions.fixedPriceRevenue, 'Fixed Price', format._formatCurrency(project.budgetBaseCurrency));
			// Actuals
			addColumn(
				row,
				boolEyeOptions.actualRevenue,
				formatMessage({id: 'project_budget.actual_revenue'}),
				format._formatCurrency(financialNumbers.actualRevenue)
			);
			addColumn(row, boolEyeOptions.actualCost, 'Actual Cost', format._formatCurrency(financialNumbers.actualCost));
			addColumn(row, boolEyeOptions.actualProfit, 'Actual Profit', format._formatCurrency(financialNumbers.actualProfit));
			addColumn(
				row,
				boolEyeOptions.actualMargin,
				'Actual Margin',
				format._formatPercentageFromFraction(financialNumbers.actualMargin)
			);
			// Plan
			addColumn(
				row,
				boolEyeOptions.planRevenue,
				formatMessage({id: 'project_budget.planned_revenue'}),
				format._formatCurrency(financialNumbers.plannedRevenue)
			);
			addColumn(row, boolEyeOptions.planCost, 'Planned Cost', format._formatCurrency(financialNumbers.plannedCost));
			addColumn(row, boolEyeOptions.planProfit, 'Planned Profit', format._formatCurrency(financialNumbers.plannedProfit));
			addColumn(
				row,
				boolEyeOptions.planMargin,
				'Plan Margin',
				format._formatPercentageFromFraction(financialNumbers.plannedMargin)
			);
			// Remaining
			addColumn(
				row,
				boolEyeOptions.remainingRevenue,
				formatMessage({id: 'project_budget.remaining_revenue'}),
				format._formatCurrency(financialNumbers.remainingRevenue)
			);
			addColumn(
				row,
				boolEyeOptions.remainingCost,
				formatMessage({id: 'project_budget.remaining_cost'}),
				format._formatCurrency(financialNumbers.remainingCost)
			);
			addColumn(
				row,
				boolEyeOptions.remainingProfit,
				formatMessage({id: 'project_budget.remaining_profit'}),
				format._formatCurrency(financialNumbers.remainingProfit)
			);
			addColumn(
				row,
				boolEyeOptions.remainingMargin,
				formatMessage({id: 'project_budget.remaining_margin'}),
				format._formatPercentageFromFraction(financialNumbers.remainingMargin)
			);
			// Forecasted
			addColumn(
				row,
				boolEyeOptions.forecastedRevenue,
				formatMessage({id: 'project_budget.forecast_revenue'}),
				format._formatCurrency(financialNumbers.forecastRevenue)
			);
			addColumn(
				row,
				boolEyeOptions.forecastedCost,
				formatMessage({id: 'project_budget.forecast_cost'}),
				format._formatCurrency(financialNumbers.forecastCost)
			);
			addColumn(
				row,
				boolEyeOptions.forecastedProfit,
				formatMessage({id: 'project_budget.forecast_profit'}),
				format._formatCurrency(financialNumbers.forecastProfit)
			);
			addColumn(
				row,
				boolEyeOptions.forecastedMargin,
				formatMessage({id: 'project_budget.forecast_margin'}),
				format._formatPercentageFromFraction(financialNumbers.forecastMargin)
			);
			// Actual vs. Plan
			addColumn(
				row,
				boolEyeOptions.actualPlanRevenue,
				formatMessage({id: 'project_budget.actual_vs_plan'}) + ' ' + formatMessage({id: 'project_budget.revenue'}),
				format._formatCurrency(financialNumbers.actualVsPlanRevenue)
			);
			addColumn(
				row,
				boolEyeOptions.actualPlanCost,
				'Actual vs. Planned Cost',
				format._formatCurrency(financialNumbers.actualVsPlanCost)
			);
			addColumn(
				row,
				boolEyeOptions.actualPlanProfit,
				'Actual vs. Planned Profit',
				format._formatCurrency(financialNumbers.actualVsPlanProfit)
			);
			// Forecasted vs. Plan
			addColumn(
				row,
				boolEyeOptions.forecastedPlanRevenue,
				formatMessage({id: 'project_budget.forecast_vs_plan'}) + ' ' + formatMessage({id: 'project_budget.revenue'}),
				format._formatCurrency(financialNumbers.forecastVsPlanRevenue)
			);
			addColumn(
				row,
				boolEyeOptions.forecastedPlanCost,
				formatMessage({id: 'project_budget.forecast_vs_plan'}) + ' ' + formatMessage({id: 'project_budget.cost'}),
				format._formatCurrency(financialNumbers.forecastVsPlanCost)
			);
			addColumn(
				row,
				boolEyeOptions.forecastedPlanProfit,
				formatMessage({id: 'project_budget.forecast_vs_plan'}) + ' ' + formatMessage({id: 'project_budget.profit'}),
				format._formatCurrency(financialNumbers.forecastVsPlanProfit)
			);
			addColumn(
				row,
				boolEyeOptions.retainerPeriodTargetMinutes,
				'Retainer Period Target Minutes',
				format._formatNumber(financialNumbers.retainerPeriodTargetMinutes)
			);
			addColumn(
				row,
				boolEyeOptions.retainerPeriodTargetPrice,
				'Retainer Period Target Price',
				format._formatCurrency(financialNumbers.retainerPeriodTargetPrice)
			);
			// Currency
			addColumn(row, true, 'Currency', currency);

			return row;
		});

		const headerRow = Object.keys(periodData[0]).reduce((map, column) => {
			map[column] = column;
			return map;
		}, {});
		periodData.splice(0, 0, headerRow);

		const csv = Util.JSONToCSV(periodData);
		Util.exportToCSV(csv, `project_portfolio_period_${startDate.format('YYYY-MM-DD')}-${endDate.format('YYYY-MM-DD')}.csv`);
	}
};

const ProjectPortfolioPeriodDownload = ({viewer, closeModal, startDate, endDate, boolEyeOptions}) => {
	const intl = useIntl();

	useEffect(() => {
		if (viewer) {
			exportToCsv(startDate, endDate, viewer, boolEyeOptions, intl);
			closeModal();
		}
	}, [viewer]);

	return null;
};

export const projectPortfolioPeriodDownloadQuery = graphql`
	query ProjectPortfolioPeriodDownload_Query(
		$startYear: Int!
		$startMonth: Int!
		$startDay: Int!
		$endYear: Int!
		$endMonth: Int!
		$endDay: Int!
		$searchQuery: TaskSearchQueryType!
		$pageSize: Int!
		$cursor: String
	) {
		viewer {
			actualPersonId
			component(name: "project_portfolio_period_project_csv_loader")
			...ProjectPortfolioPeriodDownload_viewer
				@arguments(
					searchQuery: $searchQuery
					pageSize: $pageSize
					cursor: $cursor
					startYear: $startYear
					startMonth: $startMonth
					startDay: $startDay
					endYear: $endYear
					endMonth: $endMonth
					endDay: $endDay
				)
		}
	}
`;

export default createFragmentContainer(ProjectPortfolioPeriodDownload, {
	viewer: graphql`
		fragment ProjectPortfolioPeriodDownload_viewer on Viewer
		@argumentDefinitions(
			searchQuery: {type: "TaskSearchQueryType!"}
			startYear: {type: "Int!"}
			startMonth: {type: "Int!"}
			startDay: {type: "Int!"}
			endYear: {type: "Int!"}
			endMonth: {type: "Int!"}
			endDay: {type: "Int!"}
			pageSize: {type: "Int!"}
			cursor: {type: "String"}
		) {
			id
			company {
				currency
				financialBasedProjects(
					searchQuery: $searchQuery
					startYear: $startYear
					startMonth: $startMonth
					startDay: $startDay
					endYear: $endYear
					endMonth: $endMonth
					endDay: $endDay
					first: $pageSize
					after: $cursor
				) {
					edges {
						node {
							id
							companyProjectId
							name
							budgetType
							budgetBaseCurrency
							defaultPeriodBudgetType
							fullAccessToProject
							projectColor
							status
							projectStartDay
							projectStartMonth
							projectStartYear
							projectEndDay
							projectEndMonth
							projectEndYear
							baselineWinChance
							completion
							progressEndOfTheMonth
							useBaseline
							rateCard {
								id
								name
								currency
								parentRateCardId
							}
							client {
								id
								name
								logoId
								logoDefaultId
							}
							projectLabels(first: 1000) {
								edges {
									node {
										label {
											name
											color
										}
									}
								}
							}
							projectPersons(first: 1000000, contactsOnly: true) {
								edges {
									node {
										id
										isContactPerson
										person {
											id
											fullName
											firstName
											lastName
											profilePictureId
											profilePictureDefaultId
											initials
										}
									}
								}
							}
							currentProjectStatus {
								id
								color
								person {
									id
									firstName
									lastName
									fullName
									profilePictureId
								}
							}
							financialNumbers(
								startYear: $startYear
								startMonth: $startMonth
								startDay: $startDay
								endYear: $endYear
								endMonth: $endMonth
								endDay: $endDay
							) {
								actualRevenue
								actualCost
								actualProfit
								actualMargin
								plannedRevenue
								plannedCost
								plannedProfit
								plannedMargin
								remainingRevenue
								remainingCost
								remainingProfit
								remainingMargin
								forecastRevenue
								forecastCost
								forecastProfit
								forecastMargin
								baselineRevenue
								baselineCost
								baselineProfit
								baselineMargin
								baselineMinutes
								actualVsPlanRevenue
								actualVsPlanCost
								actualVsPlanProfit
								forecastVsPlanRevenue
								forecastVsPlanCost
								forecastVsPlanProfit
								registeredMinutes
								remainingMinutes
								forecastMinutes
								scopeApprovedMinutes
								scopeTotalMinutes
								allocationMinutes
								invoiced
								paid
								invoicedVsActualRevenue
								retainerPeriodTargetMinutes
								retainerPeriodTargetPrice
								lockedRevenue
								recognitionOpenRevenue
								recognitionAmount
							}
						}
					}
				}
			}
		}
	`,
});
